/*
 * 统一 HTTP 请求层：所有后端接口调用的唯一入口
 *
 * 职责：
 * - 统一拼接 VUE_APP_BASE_API、统一 JSON 序列化/解析；
 * - 统一超时（默认 15s，AbortController）；
 * - 统一附带 userSessionId header，后端 reco_api_log 切面据此完成全链路日志关联；
 * - 统一响应解包：后端 AjaxResult 结构（{code,msg,data}）成功时直接返回 data，
 *   失败时抛 ApiError；旧数据源的透传结构（无 code 字段）原样返回，保证灰度兼容；
 * - 统一请求日志（接口名、方法、耗时、状态）与错误日志（logger）；
 * - 可选 auth: 'portal' 走 sessionManager 的 Bearer token（401 自动重试）。
 *
 * @Author: Mustafa.Yin
 * @Date: 2026-10-31
 */
import logger from '@/utils/logger';
import { ensurePortalToken, clearPortalToken, getUserSessionId } from '@/utils/sessionManager';

const DEFAULT_TIMEOUT = 15000;

/** 业务错误：后端返回非 200 code 或网络/解析失败 */
export class ApiError extends Error {
  constructor(message, { code, url, status } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.url = url;
    this.status = status;
  }
}

/** 拼接完整 URL（相对路径加 BASE_API 前缀，绝对地址原样） */
function buildUrl(url, params) {
  const base = process.env.VUE_APP_BASE_API || '';
  let fullUrl = url.startsWith('http') ? url : `${base}${url}`;
  if (params && Object.keys(params).length > 0) {
    // 剔除值为 null/undefined 的参数，避免拼出 "key=null"
    const query = Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    if (query) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + query;
    }
  }
  return fullUrl;
}

/** 解析响应：AjaxResult 解包返回 data；旧透传结构（无 code）原样返回 */
function unwrap(result, url) {
  if (result !== null && typeof result === 'object' && 'code' in result) {
    if (result.code === 200) {
      return result.data;
    }
    throw new ApiError(result.msg || '接口返回异常', { code: result.code, url });
  }
  return result;
}

async function doFetch(fullUrl, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), options.timeout || DEFAULT_TIMEOUT);
  try {
    return await fetch(fullUrl, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 通用请求
 * @param {string} url 接口路径（如 'smartreco/api/smartreco'）
 * @param {object} opts
 * @param {string}  opts.method     请求方法，默认 'get'
 * @param {object}  opts.data       请求体（自动 JSON 序列化）
 * @param {object}  opts.params     query 参数（自动剔除空值）
 * @param {object}  opts.headers    额外 header
 * @param {number}  opts.timeout    超时毫秒数，默认 15000
 * @param {string}  opts.auth       'portal' 时自动附带 Bearer token（401 重试一次）
 * @param {boolean} opts.rawText    true 时返回原始文本（如 upload 场景自行处理）
 * @param {string}  opts.apiName    日志中展示的接口名（默认取 url 尾段）
 * @returns {Promise<any>} 后端 AjaxResult 的 data（或旧结构原样）
 */
export async function request(url, opts = {}) {
  const {
    method = 'get',
    data,
    params,
    headers = {},
    timeout = DEFAULT_TIMEOUT,
    auth,
    rawText = false,
    apiName = url.replace(/^.*\//, '').split('?')[0] || url,
  } = opts;

  const fullUrl = buildUrl(url, params);
  const finalHeaders = { ...headers };
  // 全链路标识：后端 reco_api_log 切面优先读该 header
  if (!finalHeaders.userSessionId) {
    finalHeaders.userSessionId = getUserSessionId();
  }
  let body;
  if (data !== undefined && data !== null) {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      // FormData（文件上传）：不设 Content-Type，由浏览器自动携带 multipart boundary
      body = data;
    } else {
      finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
      body = typeof data === 'string' ? data : JSON.stringify(data);
    }
  }

  let doRequest = () => doFetch(fullUrl, { method, headers: finalHeaders, body, timeout });

  if (auth === 'portal') {
    const mdmNo = opts.mdmNo;
    // 自动附带 Bearer token；401 时清 token 重新初始化并重试一次
    doRequest = async () => {
      const token = await ensurePortalToken(mdmNo);
      const headersWithToken = { ...finalHeaders, Authorization: `Bearer ${token}` };
      let resp = await doFetch(fullUrl, { method, headers: headersWithToken, body, timeout });
      if (resp.status === 401) {
        clearPortalToken();
        const newToken = await ensurePortalToken(mdmNo);
        resp = await doFetch(fullUrl, {
          method,
          headers: { ...headersWithToken, Authorization: `Bearer ${newToken}` },
          body,
          timeout,
        });
      }
      return resp;
    };
  }

  const startTime = Date.now();
  let resp;
  try {
    resp = await doRequest();
  } catch (e) {
    const isAbort = e && e.name === 'AbortError';
    logger.error(`api:${apiName}`, `${method.toUpperCase()} ${url} ${isAbort ? '请求超时' : '网络异常'}`, e);
    throw new ApiError(isAbort ? '请求超时，请重试' : '网络异常，请检查网络后重试', { url, status: -1 });
  }

  const cost = Date.now() - startTime;
  if (!resp.ok) {
    logger.error(`api:${apiName}`, `${method.toUpperCase()} ${url} HTTP ${resp.status} ${cost}ms`);
    throw new ApiError(`接口异常（HTTP ${resp.status}）`, { url, status: resp.status });
  }

  const text = await resp.text();
  if (rawText) {
    logger.debug(`api:${apiName}`, `${method.toUpperCase()} ${url} ${resp.status} ${cost}ms raw`);
    return text;
  }

  let result;
  try {
    result = JSON.parse(text);
  } catch (e) {
    logger.error(`api:${apiName}`, `${method.toUpperCase()} ${url} 响应解析失败`, text && text.substring(0, 200));
    throw new ApiError('响应解析失败', { url, status: resp.status });
  }

  logger.debug(`api:${apiName}`, `${method.toUpperCase()} ${url} ${resp.status} ${cost}ms`);
  return unwrap(result, url);
}

/** GET 便捷方法 */
export function get(url, params, opts = {}) {
  return request(url, { ...opts, method: 'get', params });
}

/** POST 便捷方法 */
export function post(url, data, opts = {}) {
  return request(url, { ...opts, method: 'post', data });
}

export default request;
